import { AxiosResponse } from 'axios';
import { http, defaultConfig, getUniversityUrl } from './http';

export interface LoginPayload {
  username: string;
  password: string;
  captchaText?: string;
  validation?: string;
  nonce?: string;
}

export interface ChangePasswordPayload {
  saminKey?: string;
  password?: string;
  newPassword?: string;
  repeatNewPassword?: string;
  changePasswordOnLogin?: boolean;
}

export interface ChangeCVV2Payload {
  newCvv2: string;
  confirmNewCvv2: string;
  amount?: number;
  description?: string;
  receiptNumber?: string;
}

export interface LoginResponse {
  access_token: string;
  expires_in: number;
  first_name: string;
  jti: string;
  last_name: string;
  national_code: string;
  refresh_token: string;
  scope: string;
  token_type: string;
  user_id: number;
}

export interface SubsidyItem {
  id: number;
  createDate: string;
  credit: number;
  creditType: string;
  expirationDate: string;
  creditChanges: any[];
}

export interface NurtureProfile {
  credit: number;
  createDate: string;
  user: UserProfileEntity;
  cvv2?: number;
  subsidyCredit?: number;
  subsidies: SubsidyItem[];
  creditChanges: {
    amount: number,
    createDate: string,
    receipt: {
      receiptNumber: string,
      paymentSystemName: string,
    }
  }[];
}

export interface AuthorizeUserPayload {
  username: string;
  captchaEnteredByUser: string;
  captchaValidationCode: string;
  nonce: string;
}

export const getUserProfile = (): Promise<AxiosResponse<HttpResponse<UserProfileEntity>>> => {
  const baseURL = getUniversityUrl();
  return http.get('/rest/users/me', defaultConfig(baseURL));
};

export async function authorize({ username, password , captchaText, validation, nonce }: LoginPayload) {
  const baseURL = getUniversityUrl();
  const data = new URLSearchParams();
  data.append('username', username);
  data.append('password', password);
  data.append('captchaText', captchaText);
  data.append('validation', validation);
  data.append('nonce', nonce);
  data.append('grant_type', 'password');
  data.append('scope', 'read write');

  return http.post<LoginResponse>('/oauth/token', data, {
    ...defaultConfig(baseURL, {
      auth: false,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
        'authorization': 'Basic c2FtYWQtbW9iaWxlOnNhbWFkLW1vYmlsZS1zZWNyZXQ=',
      }
    })
  });
}


export async function getPasswordConfig(username: string) {
  const baseURL = getUniversityUrl();
  return (await http.get(`/rest/users/password-configs?username=${username}`, defaultConfig(baseURL))).data;
}


export async function gererateCaptcha({ nonce }: { nonce: string }) {
  const baseURL = getUniversityUrl();
  return http.post(`/rest/captcha`, { nonce }, defaultConfig(baseURL, { auth: false, headers: { 'Content-Type': 'application/json' } }));
}

export async function authorizeUser(payload: AuthorizeUserPayload) {
  const baseURL = getUniversityUrl();
  return http.post(`/rest/users/reset-password/init`, payload, defaultConfig(baseURL, { auth: false, headers: { 'Content-Type': 'application/json' } }));
}

export async function changePassword(userId: string, payload: ChangePasswordPayload) {
  const baseURL = getUniversityUrl();
  return http.put(`/rest/users/${userId}/change-password`, payload, defaultConfig(baseURL));
}

export async function changeMePassword(payload: ChangePasswordPayload) {
  const baseURL = getUniversityUrl();
  return http.put('/rest/users/me/change-password', payload, defaultConfig(baseURL));
}

export async function fetchNurtureProfile(): Promise<HttpResponse<NurtureProfile>> {
  const baseURL = getUniversityUrl();
  return (await http.get('/rest/users/nurture-profiles', defaultConfig(baseURL))).data;
}

export async function changeCvv2(payload: ChangeCVV2Payload) {
  const baseURL = getUniversityUrl();
  return http.put('/rest/users/nurture-profiles/change-cvv2', payload, defaultConfig(baseURL));
}

export async function loginToken() {
  const baseURL = getUniversityUrl();
  return http.get<{ payload: { loginAsToken: string; } }>('/rest/users/me/login-token', defaultConfig(baseURL));
}

export async function redirectToOld(loginToken: string) {
  const baseURL = getUniversityUrl();
  window.open(`${baseURL}/j_security_check?loginAsToken=${loginToken}&redirect=/`, '_self');
}

export async function listTeammate(): Promise<{ payload: HttpResponsePayload<TeammateEntity[]> }> {
  const baseURL = getUniversityUrl();
  return (await (http.get('/rest/nurtures/users/teammates?page=0&size=999', defaultConfig(baseURL)))).data;
}

export async function removeTeammate(id: string | number) {
  const baseURL = getUniversityUrl();
  return (http.delete(`/rest/nurtures/users/teammates/${id}`, defaultConfig(baseURL)));
}

export async function addTeammate(username: string) {
  const baseURL = getUniversityUrl();
  return (await http.post('/rest/nurtures/users/teammates', { username }, defaultConfig(baseURL))).data;
}

export async function joinTeammate(id: string | number) {
  const baseURL = getUniversityUrl();
  return (await http.put(`/rest/nurtures/users/teammates/${id}`, {
    confirmation: true
  }, defaultConfig(baseURL))).data;
}

export async function fetchUserById(id: string | number) {
  const baseURL = getUniversityUrl();
  return (await http.get(`/rest/users/${id}`, defaultConfig(baseURL))).data;
}

export async function fetchUserByUsername(username: string) {
  const baseURL = getUniversityUrl();
  return (await http.get(`/rest/users?username=${username}`, defaultConfig(baseURL))).data;
}

export async function uploadProfilePicture(data: { username: string, userImageBase64: string }) {
  const baseURL = getUniversityUrl();
  return (await http.post('/rest/profile-pictures/upload', data, defaultConfig(baseURL))).data;
}

export async function logout(username: string) {
  const baseURL = getUniversityUrl();
  return (await http.post(`/rest/oauth/${username}/logout`, undefined, defaultConfig(baseURL, {
    auth: false,
    headers: {
      'Accept': '*/*'
    }
  }))).data;
}
